import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from './useAuth'
import { showToast } from '../utils/toast'

export function useFollows() {
  const [following, setFollowing] = useState<string[]>([])
  const [followers, setFollowers] = useState<string[]>([])
  const [topicFollows, setTopicFollows] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const { user } = useAuth()

  const fetchFollows = async () => {
    if (!user) {
      setLoading(false)
      return
    }

    try {
      // Get users I'm following
      const { data: followingData } = await supabase
        .from('follows')
        .select('following_id')
        .eq('follower_id', user.id)

      // Get my followers
      const { data: followersData } = await supabase
        .from('follows')
        .select('follower_id')
        .eq('following_id', user.id)

      // Get topics I'm following
      const { data: topicData } = await supabase
        .from('topic_follows')
        .select('topic')
        .eq('user_id', user.id)

      setFollowing(followingData?.map(f => f.following_id) || [])
      setFollowers(followersData?.map(f => f.follower_id) || [])
      setTopicFollows(topicData?.map(t => t.topic) || [])
    } catch (error) {
      console.error('Error fetching follows:', error)
      showToast.system.error('Failed to load follow data')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchFollows()
  }, [user])

  const followUser = async (userId: string, userName?: string) => {
    if (!user) {
      showToast.auth.authError('Please sign in to follow users')
      return { error: 'Not authenticated' }
    }

    try {
      // Validate that userId is a valid UUID
      if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(userId)) {
        throw new Error(`Invalid UUID format: ${userId}`)
      }

      const { data, error } = await supabase
        .from('follows')
        .insert({
          follower_id: user.id,
          following_id: userId
        })

      if (error) throw error
      
      setFollowing(prev => [...prev, userId])
      showToast.community.followSuccess(userName || 'user')
      return { data, error: null }
    } catch (error: any) {
      console.error('Error following user:', error)
      showToast.system.error('Failed to follow user: ' + error.message)
      return { data: null, error }
    }
  }

  const unfollowUser = async (userId: string, userName?: string) => {
    if (!user) {
      showToast.auth.authError('Please sign in to unfollow users')
      return { error: 'Not authenticated' }
    }

    try {
      // Validate that userId is a valid UUID
      if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(userId)) {
        throw new Error(`Invalid UUID format: ${userId}`)
      }

      const { error } = await supabase
        .from('follows')
        .delete()
        .eq('follower_id', user.id)
        .eq('following_id', userId)

      if (error) throw error
      
      setFollowing(prev => prev.filter(id => id !== userId))
      showToast.community.unfollowSuccess(userName || 'user')
      return { error: null }
    } catch (error: any) {
      console.error('Error unfollowing user:', error)
      showToast.system.error('Failed to unfollow user: ' + error.message)
      return { error }
    }
  }

  const followTopic = async (topic: string) => {
    if (!user) {
      showToast.auth.authError('Please sign in to follow topics')
      return { error: 'Not authenticated' }
    }

    try {
      const { data, error } = await supabase
        .from('topic_follows')
        .insert({
          user_id: user.id,
          topic
        })

      if (error) throw error
      
      setTopicFollows(prev => [...prev, topic])
      showToast.system.success(`Now following ${topic} topic! 📚`)
      return { data, error: null }
    } catch (error) {
      console.error('Error following topic:', error)
      showToast.system.error('Failed to follow topic')
      return { data: null, error }
    }
  }

  const unfollowTopic = async (topic: string) => {
    if (!user) {
      showToast.auth.authError('Please sign in to unfollow topics')
      return { error: 'Not authenticated' }
    }

    try {
      const { error } = await supabase
        .from('topic_follows')
        .delete()
        .eq('user_id', user.id)
        .eq('topic', topic)

      if (error) throw error
      
      setTopicFollows(prev => prev.filter(t => t !== topic))
      showToast.system.success(`Unfollowed ${topic} topic`)
      return { error: null }
    } catch (error) {
      console.error('Error unfollowing topic:', error)
      showToast.system.error('Failed to unfollow topic')
      return { error }
    }
  }

  const isFollowing = (userId: string) => following.includes(userId)
  const isFollowingTopic = (topic: string) => topicFollows.includes(topic)

  return {
    following,
    followers,
    topicFollows,
    loading,
    followUser,
    unfollowUser,
    followTopic,
    unfollowTopic,
    isFollowing,
    isFollowingTopic,
    refreshFollows: fetchFollows
  }
}